import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, ArrowRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useContent, FOUNDER_PHOTOS } from "../../cms/ContentContext";
import { track } from "../../lib/analytics";
import { FOUNDER_BIOS } from "../../data/content";

function FounderFace({ m }) {
  const photo = m.photo || FOUNDER_PHOTOS[m.initials];
  const id = (m.initials || m.name).toLowerCase();
  const bio = m.bio || FOUNDER_BIOS[m.initials];
  return (
    <Popover onOpenChange={(o) => o && track("founder_spotlight_opened", { founder: m.name })}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={`About ${m.name}`} data-testid={`founder-face-${id}`}
          className="relative h-10 w-10 rounded-full ring-2 ring-background transition-transform duration-200 hover:-translate-y-0.5 hover:z-10 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-primary data-[state=open]:z-10 data-[state=open]:ring-primary">
          {photo
            ? <img src={photo} alt={m.name} loading="lazy" className="h-full w-full rounded-full object-cover" />
            : <span className="h-full w-full rounded-full bg-primary/12 text-primary inline-flex items-center justify-center text-xs font-semibold">{m.initials}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" sideOffset={10} collisionPadding={16} className="w-[min(20rem,calc(100vw-2rem))] rounded-lg p-0 overflow-hidden" data-testid={`founder-card-${id}`}>
        <div className="flex items-center gap-3 border-b border-border p-4">
          {photo && <img src={photo} alt={m.name} className="h-12 w-12 rounded-full object-cover" />}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground" data-testid="founder-card-name">{m.name}</p>
            <p className="text-xs text-primary">{m.role}</p>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {bio && <p className="text-sm leading-relaxed text-muted-foreground" data-testid="founder-card-bio">{bio}</p>}
          <div className="flex items-center justify-between gap-3">
            {m.linkedin && (
              <a href={m.linkedin} target="_blank" rel="noopener noreferrer" data-testid="founder-card-linkedin"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
                <Linkedin className="h-3.5 w-3.5" /> LinkedIn
              </a>
            )}
            <Link to="/company" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="founder-card-team-link">
              Meet the team <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function FounderSpotlight() {
  const { content } = useContent();
  return (
    <div className="mt-8 flex flex-col items-start gap-2.5 sm:flex-row sm:items-center sm:gap-3" data-testid="founder-spotlight">
      <div className="flex -space-x-2.5 shrink-0">
        {(content.team || []).slice(0, 4).map((m) => <FounderFace key={m.name} m={m} />)}
      </div>
      <p className="min-w-0 text-sm text-muted-foreground">
        Built by founders from financial services, security and AI.{" "}
        <span className="text-xs text-muted-foreground/70">Tap a face to meet them.</span>
      </p>
    </div>
  );
}
